// ============================================================
// useLoadoutLibrary - session-shared named weapon kits.
//
// Loadouts are the answer to "give these five Guardsmen the same
// lasgun". A combatant's weapons live on its own sheet (that is what
// makes the roster work), so a loadout is a TEMPLATE you stamp onto a
// combatant - not a live link. Stamping copies the weapons in; editing
// the combatant afterwards does not edit the loadout, and vice versa.
//
// Solo sessions keep them in localStorage; a multiplayer session syncs
// them through sessions/{id}/loadouts so the whole table can share a kit.
// ============================================================
import { useCallback, useEffect, useState, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { CharacterWeapon } from '../engine/character/types';
import type { CollectionRead } from '../backend/types';
import type { Combatant, Loadout } from '../engine/roster/types';
import { loadoutFromWeapons } from '../engine/roster/factories';
import deepClone from '../utils/deepClone';
import { remapUnsafeIds } from '../engine/importIds';

const STORAGE_KEY = 'dw-loadouts';

interface LoadoutMp {
  onLoadoutsUpdate?: (sessionId: string, cb: (read: CollectionRead<Record<string, unknown>>) => void) => () => void;
  setLoadout?: (sessionId: string, id: string, loadout: Loadout, playerId: string | null) => Promise<void>;
  deleteLoadout?: (sessionId: string, id: string) => Promise<void>;
}

interface Params {
  sessionId: string | null;
  playerId: string | null;
  mp: LoadoutMp;
}

export interface UseLoadoutLibraryReturn {
  loadouts: Loadout[];
  /** Save a combatant's current weapons as a named kit. */
  saveLoadout: (name: string, weapons: CharacterWeapon[]) => Loadout;
  deleteLoadout: (id: string) => void;
  /** Stamp a kit onto a combatant - a copy, not a live link. */
  applyLoadout: (combatant: Combatant, loadout: Loadout) => Combatant;
}

/**
 * The local shelf. It is shown in a session too, where deleting a kit writes
 * `loadouts/{id}` - so an id stored before the import check (2026-10-03) is
 * replaced on load, as an imported one is (`importIds`), and the shelf
 * rewritten once so the fix sticks.
 */
export function readLocal(): Loadout[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const kits = raw ? (JSON.parse(raw) as Loadout[]) : [];
    const safe = remapUnsafeIds(
      kits,
      kits.map((k) => k?.id),
    );
    if (safe !== kits) writeLocal(safe);
    return safe;
  } catch {
    return [];
  }
}

function writeLocal(loadouts: Loadout[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loadouts));
  } catch {
    /* quota or private mode - the session copy still works */
  }
}

const byName = (a: Loadout, b: Loadout) => a.name.localeCompare(b.name);

/** The kits in a read, skipping a corrupt one rather than losing the rest. */
function parseKits(entries: CollectionRead<Record<string, unknown>>['entries']): Loadout[] {
  const kits: Loadout[] = [];
  for (const d of entries) {
    try {
      const raw = d.data.data as string | undefined;
      if (raw) kits.push(JSON.parse(raw) as Loadout);
    } catch {
      /* skip a corrupt kit rather than lose the rest */
    }
  }
  return kits;
}

/**
 * The kits to show for a read. The same rule as the character library: a
 * snapshot with writes still in flight lists only some of them, and a kit
 * saved a moment ago must not vanish because the server has not mentioned
 * it yet. Only a settled view may delete (v8.25.0).
 */
function kitsToShow(read: CollectionRead<Record<string, unknown>>, shown: Loadout[]): Loadout[] {
  const next = parseKits(read.entries);
  if (!read.listsEveryone) {
    const removed = new Set(read.removed);
    const seen = new Set(next.map((l) => l.id));
    next.push(...shown.filter((local) => !seen.has(local.id) && !removed.has(local.id)));
  }
  return next.sort(byName);
}

/**
 * Stamp a kit onto a combatant.
 *
 * The weapons are deep-cloned with FRESH ids: two combatants carrying
 * "the same" bolter must track ammo separately, so they cannot share a
 * weapon id. slotOrder is cleared for the same reason - the old grid
 * arrangement refers to weapons that are gone.
 */
function applyLoadout(combatant: Combatant, loadout: Loadout): Combatant {
  const weapons = deepClone(loadout.weapons).map((w, i) => ({
    ...w,
    id: `${w.id}-${combatant.id}-${i}`,
  }));
  const weaponAmmo: Combatant['state']['weaponAmmo'] = {};
  for (const w of weapons) weaponAmmo[w.id] = { ...w.ammo, current: w.ammo.max };

  return {
    ...combatant,
    sheet: { ...combatant.sheet, weapons, updatedAt: Date.now() },
    state: { ...combatant.state, weaponAmmo },
    slotOrder: undefined,
    loadoutRef: { id: loadout.id, name: loadout.name },
    updatedAt: Date.now(),
  };
}

export default function useLoadoutLibrary({ sessionId, playerId, mp }: Params): UseLoadoutLibraryReturn {
  const [loadouts, setLoadouts] = useState<Loadout[]>(() => readLocal());
  /** What we last showed - the only way to keep an in-flight kit visible. */
  const loadoutsRef = useRef<Loadout[]>(loadouts);
  loadoutsRef.current = loadouts;

  // ---- Session sync ----
  useEffect(() => {
    if (!sessionId || !mp.onLoadoutsUpdate) return;
    const unsub = mp.onLoadoutsUpdate(sessionId, (read) => {
      const next = kitsToShow(read, loadoutsRef.current);
      loadoutsRef.current = next;
      setLoadouts(next);
    });
    return () => unsub();
  }, [sessionId, mp]);

  const { saveLoadout, deleteLoadout } = useLoadoutWrites({ sessionId, playerId, mp }, setLoadouts);
  return { loadouts, saveLoadout, deleteLoadout, applyLoadout };
}

/** Replace the shown list; a solo session keeps it in localStorage too. */
function useShownLoadouts(sessionId: string | null, setLoadouts: Dispatch<SetStateAction<Loadout[]>>) {
  return useCallback(
    (change: (prev: Loadout[]) => Loadout[]) =>
      setLoadouts((prev) => {
        const next = change(prev);
        if (!sessionId) writeLocal(next);
        return next;
      }),
    [sessionId, setLoadouts],
  );
}

/** Save and delete: the shown list at once, then localStorage (solo) or the session. */
function useLoadoutWrites({ sessionId, playerId, mp }: Params, setLoadouts: Dispatch<SetStateAction<Loadout[]>>) {
  const show = useShownLoadouts(sessionId, setLoadouts);
  const saveLoadout = useCallback(
    (name: string, weapons: CharacterWeapon[]): Loadout => {
      const loadout = loadoutFromWeapons(name, weapons);
      // Same name replaces - "Save as Devastator" twice means one kit.
      show((prev) => [...prev.filter((l) => l.name !== name), loadout].sort(byName));
      if (sessionId && mp.setLoadout) mp.setLoadout(sessionId, loadout.id, loadout, playerId).catch(console.error);
      return loadout;
    },
    [sessionId, playerId, mp, show],
  );
  const deleteLoadout = useCallback(
    (id: string) => {
      show((prev) => prev.filter((l) => l.id !== id));
      if (sessionId && mp.deleteLoadout) mp.deleteLoadout(sessionId, id).catch(console.error);
    },
    [sessionId, mp, show],
  );
  return { saveLoadout, deleteLoadout };
}
